import {
  acceptsDigits, aliasStatus, answerRefEligibility, answerSourceKey, collectAliasUsages, familyNameOf,
  aliasDrivenRowId, drivenRowIdFor, parseDriverAddress,
  lastAnswersFor, resolveAlias,
  acceptsUncertaintyDigits, answerChoicesFor, applyAnswerFormat, defaultFormatFor,
  formatsFor, maxDigitsFor, resolveAnswerRef,
  resolvedAnswerNumber, rerollAnswerInTier,
  type AnswerTier, type ReachSite,
  sampleCorrectAnswer,
  type AnswerChoice, type AnswerFormat,
  type AnswerRef, type AnswerRefAlias, type AnswerRefStatus, type ResolvedAnswer,
} from '@agavi/blocks';
import { createContext, useContext, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { stripHtmlForLabel } from './formula-plaintext';

import type { BlockIssue } from './block-issues';
import type { Block, Task } from '@/types/experience';

import { useRecallStandIns, useRunSession } from '@/components/preview-session-context';
import { brokenRefIssues, conditionalFormulaRefIssues } from '@/lib/recall-issues';

/**
 * WHAT A TEACHER NEEDS TO KNOW ABOUT A REFERENCE THEY ARE LOOKING AT.
 *
 * A chip has to say three things at once: that it is a reference, what it points at, and whether
 * that will be there when the student arrives. The first two are the chip's own; the third is a
 * question about the whole experience, which no block can answer on its own — so it is answered
 * here, once, for every chip on the task.
 *
 * ## Absent on purpose outside authoring
 *
 * Preview and the student runtime render the VALUE, not the reference, so they want none of
 * this. `useAnswerRefInfo` returns null without a provider and the chip falls back to its plain
 * form — which is also why the status iconography can never leak into what a student sees.
 */
export interface AnswerRefContextValue {
  /** Every task in the experience, which is what reachability is computed over. */
  tasks: Task[];
  /** Where the chip is being authored — the other end of the reachability question. */
  currentTaskId: string;
  /** The experience's aliases. A chip names one of these and nothing else. */
  aliases: AnswerRefAlias[];
  /**
   * The names shared by every task — what `aliasDriverSpace` needs to judge an option's words.
   *
   * Here rather than beside the task's own variables because this is the CROSS-TASK scope, and
   * every reader of it is asking a cross-task question: an option whose text is a variable of
   * one task has no fixed words to merge on, so the member is refused and the teacher is told
   * to promote it. Absent on a surface with no experience, which marks every chip task-scoped
   * and so refuses rather than admits — the safe direction.
   */
  experienceVariableNames?: ReadonlySet<string>;
  /** Find or create the alias for an address, returning its id in the same tick. */
  ensureAlias: (ref: AnswerRef) => string;
  /** Rename or re-point an alias. Universal, because there is one of these per name. */
  updateAlias: (id: string, patch: Partial<Omit<AnswerRefAlias, 'id'>>) => void;
  /**
   * Drop a name nothing cites — the manual half of the orphan sweep.
   *
   * The sweep handles the ordinary case on its own and is deliberately narrow: it fires only
   * where nothing anywhere names the alias, and only once the task has saved. That leaves
   * names already stranded by an earlier session with no way out, which is what this is for.
   * Absent on a surface that cannot write, so the control is simply not offered there.
   */
  removeAlias?: (id: string) => void;
  /** Go to a block that recalls something — another task, usually. Absent where the surface
   *  cannot navigate, which is every surface but the task editor. */
  openUsage?: (taskId: string, blockId: string) => void;
  /**
   * WHAT THE TEACHER SAID THE EARLIER ANSWER WAS, for the test card, by alias.
   *
   * The sample is a default and the correct answer is rarely the interesting one, so a
   * reference has to be settable. Held here rather than per block because an alias is one
   * name: two blocks on this task quoting the same answer must quote the same value, or the
   * teacher is reading a page no student could be shown.
   *
   * Bench state, deliberately not persisted — it belongs to this sitting the way a roll does,
   * and a stand-in written into the document would be a second answer key nobody asked for.
   */
  testAnswers?: Record<string, unknown>;
  setTestAnswer?: (aliasId: string, answer: unknown | null) => void;
  /**
   * THE ROLL A RECALLED ANSWER IS WORKED OUT AGAINST, for the editor's bench.
   *
   * Not the citing block's own roll, which is what it was at first and is arbitrary: the
   * answer being quoted belongs to ANOTHER task, so this block's draw has no claim on it.
   * Worse, two surfaces then disagreed — the sentence resolved `{river}+{kayak}` against the
   * block's roll while the control offering to change it had no roll at all, so the value on
   * screen and the value a click would set came from different numbers.
   *
   * ONE ROLL PER TASK, held by the page, and asked for BY THE SOURCE'S TASK.
   *
   * A single flat map was the first shape and carried a wrong assumption, written here as
   * "experience variables are shared by everything, which makes a single draw honest". They
   * are; task-scoped variables are not. The map was rolled from the VIEWING task's variables
   * in force, so a source block whose answer reads its own task's `{time}` met two outcomes
   * and both were wrong: nothing at all, or — if the viewing task happened to define a `time`
   * of its own — that one. A different quantity under the same name, silently, because
   * resolution is by name into one flat map and nothing in it can tell two `time`s apart.
   */
  benchVariablesFor?: (taskId: string) => Record<string, number>;
  /**
   * THE REFERENCES SOMETHING ELSE ON SCREEN ALREADY OFFERS TO SET.
   *
   * Preview's Inspect panel lists every reference this task is waiting on, each with its own
   * menu, so the control buried in the sentence is a second way to do a thing already offered
   * in one place -- and the worse of the two, because it is found by hunting for dimmed words.
   * A reference named here renders as plain text: still marked, still telling the teacher the
   * sentence is standing on a value nobody gave, but no longer a control.
   *
   * BY ALIAS, AND ONLY WHILE THE BAND IS BESIDE THE TASK. A surface that covers the task with
   * the panel, or has no panel up, passes nothing and keeps every chip settable -- the rule is
   * "something else can do this right now", and it has to stop being true the moment that
   * stops being true, or a teacher is left with a reference and nothing anywhere to set it
   * with.
   */
  bandedAliasIds?: ReadonlySet<string>;
}

const AnswerRefContext = createContext<AnswerRefContextValue | null>(null);

/**
 * THE MOMENT A SENTENCE IS BEING READ AS OF, or now.
 *
 * Exit text quotes the student back to themselves — *"you estimated 4.38 m/s but 0.2 m/s²"* —
 * and a summary being REVIEWED has to quote the answers as they stood when that exit fired.
 * Flipping back to an earlier visit and reading the latest numbers would describe work the
 * student had not done yet, in the teacher's own words.
 *
 * A separate context from `ReviewAttemptContext`, which carries the attempt a BLOCK re-renders.
 * The two answer different questions: that one is "what did this block hold", this one is "when
 * is this sentence speaking from", and a sentence can quote a block in another task entirely.
 *
 * `null` is now, which is what everything playing forward wants and therefore the default.
 */
const RecallAsOfContext = createContext<string | null>(null);

/**
 * Reads a sentence as of a moment. Wraps the exit content shown for one attempt.
 *
 * Deliberately NOT applied to the task's own blocks: a student answering is in the present, and
 * the chip in a question has to show what they have just typed.
 */
export function RecallAsOfProvider({ at, children }: { at: string | null; children: React.ReactNode }) {
  return <RecallAsOfContext.Provider value={at}>{children}</RecallAsOfContext.Provider>;
}
/**
 * IS THIS PROSE THE TASK'S EXIT, rather than one of its blocks?
 *
 * The one fact that decides whether a same-task recall can resolve. A block is on screen WITH
 * the question it quotes, so when it renders the answer does not exist yet and the reference
 * can never resolve; the exit is read after submission, when every question in the task has
 * been answered. Owner: "a task can't reference itself because the recalled response doesn't
 * exist yet. but it DOES exist after submission, and should be referenceable in the summary."
 *
 * A context rather than a prop because both readers of it are deep and neither is the exit's
 * own component: the picker decides which rows to offer, and the chip decides whether to paint
 * itself broken. `false` everywhere it is not provided, which is every block on the canvas.
 */
const AtExitContext = createContext(false);

/** Marks its subtree as the task's exit — the authoring modal and the verdict both wrap in it. */
export function AtExitProvider({ children }: { children: React.ReactNode }) {
  return <AtExitContext.Provider value={true}>{children}</AtExitContext.Provider>;
}

export function useAtExit(): boolean {
  return useContext(AtExitContext);
}

export const AnswerRefProvider = AnswerRefContext.Provider;

/*
 * The status vocabulary lives in `@agavi/blocks` beside the rule that produces it, because the
 * manager and publication gating read it too — and `conditional` carries NO warning on any of
 * those surfaces. The system can tell that a source is only sometimes reached; what it cannot
 * tell is whether the teacher meant it, and "everyone goes down that pathway anyway" is
 * information only they have.
 */
export type { AnswerRefStatus };

export interface AnswerRefInfo {
  status: AnswerRefStatus;
  /** The alias's own name, or one derived from its single member. */
  name: string;
  /** Whether that name is the teacher's or derived — the rename dialog needs to know. */
  named: boolean;
  /**
   * THE STORED NAME, with no facet suffix — what a rename field holds.
   *
   * `name` is what a chip READS, and that is the family's name plus the part of the answer
   * this one quotes: `kv (full)`. The suffix is derived and belongs to nobody, so a field
   * pre-filled with it would store it on the next commit and the next rename would produce
   * `kv (full) (full)`. The owner's rule is that the suffix is "automatically appended to
   * the chip, but never on the alias".
   */
  rawName: string;
  /** Every member's address, joined — for a tooltip and for a label that takes one string. */
  address: string;
  /** The same, apart, for a surface with room to put each on its own line. */
  addresses: string[];
  /**
   * The name it WOULD carry with no name of its own — what a rename field shows as its
   * placeholder, and what clearing one returns the alias to.
   *
   * Separate from `name` because a renamed alias still needs to know its derived name: typing
   * that name back is the same as clearing the field, and without this the field would store
   * a literal copy that then stopped following the block's own renames.
   */
  derived: string;
}

/** The facets whose name is a word; a FITB blank's facet is its own id and is numbered instead. */
const FACET_KEY: Record<string, string> = {
  /* `value` is nameless on a block that offers nothing else, and needs a word the moment it has
     siblings — a Numerical with units reads "(number)" beside "(unit)" and "(full)". */
  value: 'blocks.answerRef.facetValue',
  lat: 'blocks.answerRef.facetLat',
  lng: 'blocks.answerRef.facetLng',
  low: 'blocks.answerRef.facetLow',
  high: 'blocks.answerRef.facetHigh',
  unit: 'blocks.answerRef.facetUnit',
  uncertainty: 'blocks.answerRef.facetUncertainty',
  combined: 'blocks.answerRef.facetCombined',
};

function blockOf(tasks: Task[], ref: AnswerRef): { task?: Task; block?: Block } {
  const task = tasks.find(t => t.id === ref.taskId);
  return { task, block: task?.blocks?.find(b => b.id === ref.blockId) };
}

/**
 * WHERE A DERIVED NAME IS DERIVED FROM: the tasks, and the names already given.
 *
 * One parameter rather than two because the two must agree. A name is derived from the block
 * it quotes UNLESS a sibling facet of that block has been named, in which case the family's
 * name wins — so a caller holding the tasks and not the aliases would quietly answer the old
 * question and report the old name, which is exactly what was reported: "i changed the number
 * alias to kv and it still says kayak-vel on the full value chip".
 *
 * `AnswerRefContextValue` satisfies it as it stands, so the ordinary caller passes its scope.
 */
export interface NameSource {
  tasks: Task[];
  aliases: readonly AnswerRefAlias[];
}

/**
 * The name a chip shows when the teacher has not given it one.
 *
 * Derived rather than materialised, so renaming the block updates every chip that never got a
 * custom name, with no sweep and nothing to go stale.
 *
 * ## Only as much name as it takes
 *
 * Three parts, each earned: the block's name always, the facet when the block offers more than
 * one, and the TASK when some other task holds a referenceable block by the same name.
 *
 * The task is the part worth explaining. A chip is read inside a sentence the teacher wrote, so
 * every word it spends is a word of their prose it pushes aside — "Earlier you said
 * First task · Gas name" is a worse sentence than "Earlier you said Gas name", and the address
 * is one hover or one glance at the manager away. But block names are the teacher's, and two
 * tasks both holding a "Gas name" is not unusual; there the short form names neither. So the
 * task appears exactly when it decides something, which is the same bargain the facet suffix
 * already makes.
 *
 * ## `omitTask`, for the surfaces that already say it
 *
 * The prefix exists because a chip in prose has nothing around it to supply the task. The
 * picker and the manager both do: the picker groups its rows under a task heading, and a
 * manager row prints the task on its own line directly beneath the name. Repeating it there is
 * the word twice on one row, so those two callers ask for the short form. The chip, which has
 * no context to borrow, does not.
 */
export function answerRefDefaultName(
  src: NameSource,
  ref: AnswerRef,
  t: (k: string, o?: Record<string, unknown>) => string,
  opts?: {
    omitTask?: boolean;
    kinds?: 'any' | 'number';
    /**
     * SPELL THE BLOCK, whatever the teacher has called this answer.
     *
     * Two callers need it. An ADDRESS says where the answer came from, and answering that
     * with the name is a circle: a chip reading "kv (unit)" whose address also reads
     * "kv (unit)" has told the author nothing they did not already see. And the rename
     * field's PLACEHOLDER is what the name would fall back to if cleared, which cannot be
     * the name it is showing.
     */
    ignoreFamily?: boolean;
  },
): string {
  const { tasks, aliases } = src;
  const { task, block } = blockOf(tasks, ref);
  if (!block) return t('blocks.answerRef.missingSource');

  /*
   * A NAME GIVEN TO ANY FACET NAMES THE WHOLE BLOCK — see `familyNameOf`.
   *
   * It stands as written, task prefix and all dropped: that is what naming is for, and the
   * ambiguity the prefix exists to resolve is an ambiguity between BLOCK names. The facet
   * suffix below still applies, so a family called `kayak-vel` reads `kayak-vel (number)`,
   * `kayak-vel (uncertainty)`, `kayak-vel (unit)` and plain `kayak-vel` for the whole.
   */
  const family = opts?.ignoreFamily ? null : familyNameOf(ref, aliases);
  const own = family ?? block.name ?? t('blocks.answerRef.unnamedSource');

  // Referenceable only: a Text block sharing the name is not something a chip could have meant,
  // so letting it force a prefix would lengthen the name to rule out an impossibility.
  const ambiguous = !family && !opts?.omitTask
    && tasks.some(other => other.id !== ref.taskId && (other.blocks ?? []).some(b =>
      (b.name ?? '') === own
      && answerRefEligibility(b.type, (b.data ?? {}) as Record<string, unknown>).referenceable));
  const base = ambiguous && task?.title ? `${task.title} \u00b7 ${own}` : own;

  const eligibility = answerRefEligibility(block.type, (block.data ?? {}) as Record<string, unknown>);
  if (!eligibility.referenceable) return base;
  /*
   * A FAMILY NAME CARRIES ITS PART EVERYWHERE, whatever this surface offers.
   *
   * `kinds` narrows the suffix to the choice it resolves, which is right for a name derived
   * from the BLOCK: an equation takes numbers alone, so a Numerical with one number facet has
   * nothing to disambiguate and the block's name already says which block. A FAMILY name is
   * not disambiguating among this surface's rows — it is saying which part of a named answer
   * this is, and the other parts live on other surfaces under the same word. Suppressed, a
   * teacher met `test (full)` in prose and a bare `test` in an equation and had nothing to
   * tell them apart with. Reported as "the chip in the equation is not showing (number),
   * still".
   */
  const suffix = answerRefFacetSuffix(src, ref, t, family ? 'any' : (opts?.kinds ?? 'any'));
  return suffix ? `${base} ${suffix}` : base;
}

/**
 * WHICH PART OF THE ANSWER, parenthesised — or empty where the block has only one.
 *
 * Its own function because the suffix is NOT part of the name. The owner's rule: "(full),
 * (number), (uncertainty), and so on are automatically appended to the chip, but never on
 * the alias" — so a rename field holds `kv` and prints `(full)` beside itself, and the two
 * must be the same `(full)` the chip shows or the field is labelled with something other
 * than what it names.
 *
 * ## Only as much as the choice it resolves
 *
 * A formula offers numbers alone, so a Numerical's `(number)` separates it from a `unit` and
 * a `full` that are not on the menu — pure width in a one-line field. `(low)` and `(high)`
 * keep earning it, because both of those ARE offered there.
 *
 * Parenthesised, so the facet reads as a qualifier on the block's name rather than part of
 * it. It is also what makes the picker's nesting legible: "Numerical 1 (full)" over
 * "Numerical 1 (number)" is obviously one block's whole and part, where a space is not.
 */
export function answerRefFacetSuffix(
  src: NameSource,
  ref: AnswerRef,
  t: (k: string, o?: Record<string, unknown>) => string,
  kinds: 'any' | 'number' = 'any',
): string {
  const { block } = blockOf(src.tasks, ref);
  if (!block) return '';
  const eligibility = answerRefEligibility(block.type, (block.data ?? {}) as Record<string, unknown>);
  if (!eligibility.referenceable) return '';
  const facets = eligibility.facets.filter(f => kinds === 'any' || f.kind === kinds);
  if (facets.length < 2) return '';

  const key = FACET_KEY[ref.facet];
  if (key) return `(${t(key)})`;
  /*
   * A FITB blank, whose facet IS its id — numbered by position so the teacher reads "blank 2"
   * rather than a uuid, while the stored address stays the id and survives reordering.
   *
   * NUMBERED AMONG THE BLANKS, not among the facets. It counted facets, which was the same
   * thing until FITB gained its combined facet: that one sits first and is not a blank, so
   * every blank read one higher than it is, in the chip, the picker and the manager alike.
   * The word facets are exactly the ones that are not blanks, which is what `FACET_KEY`
   * already knows.
   */
  const blanks = facets.filter(f => !FACET_KEY[f.id]);
  const index = blanks.findIndex(f => f.id === ref.facet);
  return index < 0 ? '' : `(${t('blocks.answerRef.facetBlank', { n: index + 1 })})`;
}

/**
 * WHERE AN ANSWER COMES FROM, written out in full: task, block, and which part of its answer.
 *
 * Distinct from the NAME, which is as short as it can be — the task is dropped from a derived
 * name unless another task holds a block of the same name, because a chip in prose reading
 * "New Task - Compass 1 (bearing)" is a worse sentence than "the bearing". An address has the
 * opposite job: it answers "which answer is this", so it always says the task.
 *
 * It goes through `answerRefDefaultName` rather than joining the stored fields, because the
 * stored facet is an ID. Spelling it raw put "value" at the end of every address and a uuid at
 * the end of a FITB blank's, which is not an address anybody can read.
 */
export function answerRefAddress(
  src: NameSource,
  ref: AnswerRef,
  t: (k: string, o?: Record<string, unknown>) => string,
): string {
  const { task } = blockOf(src.tasks, ref);
  const own = answerRefDefaultName(src, ref, t, { omitTask: true, ignoreFamily: true });
  return `${task?.title ?? t('blocks.answerRef.missingTask')} \u00b7 ${own}`;
}

/**
 * THE NAME ON A SURFACE WITH ROOM FOR IT: the panel row, the driver menu, a test card.
 *
 * Everything here goes through `answerRefDefaultName`, including an alias the teacher has
 * named, because the FACET SUFFIX is not part of the name. The owner's rule: "you can only
 * change the alias for the family ... (full), (number), (uncertainty), and so on are
 * automatically appended to the chip, but never on the alias". So `kv` names the whole
 * Numerical and its four aliases read `kv`, `kv (number)`, `kv (uncertainty)`, `kv (unit)` —
 * one word to rename, and four rows nobody has to tell apart by guesswork.
 *
 * It used to return the stored name verbatim, which is what produced "i renamed it to kv, kv
 * (the number version) kept its name without any indicator what it is": two rows, one word,
 * no way to tell which was which.
 *
 * ## The first address, for an alias holding several
 *
 * They are EQUIVALENT — the same answer reached different ways — so any of them names the
 * thing, and the first is the one the teacher chose before adding alternatives. A second
 * address used to read "Unnamed reference", which is worse in authored prose than anything it
 * could have derived.
 *
 * A NUMBER was the other candidate ("Alias 1"). It carries no meaning, and it cannot be both
 * stable and consecutive: numbering by position renames every later alias when one is
 * deleted, which is the thing block auto-naming freezes names to avoid.
 */
export function aliasDisplayName(
  alias: AnswerRefAlias | undefined,
  src: NameSource,
  t: (k: string, o?: Record<string, unknown>) => string,
  opts?: { omitTask?: boolean; ignoreFamily?: boolean },
): string {
  const first = alias?.members[0];
  /* A name outliving its block still names something to the teacher reading the panel. */
  if (!alias || !first || !blockOf(src.tasks, first).block) {
    return alias?.name ?? t('blocks.answerRef.missingSource');
  }
  return answerRefDefaultName(src, first, t, opts);
}

/**
 * WHAT A CHIP READS AS, which is not the same question as what the alias is called.
 *
 * A name the teacher gave stands as written, suffix aside — that is what naming is for. A
 * name DERIVED from the source gives the full address instead, task and all, because on a
 * chip the derived name is the only thing saying which answer this is and the short form says
 * it only half: two tasks both holding a "Gas name" is ordinary, and "Gas name" then names
 * neither.
 *
 * It used to spend the task only when another task made it ambiguous, on the grounds that a
 * chip sits inside the teacher's own sentence and every word it takes is a word of theirs
 * pushed aside. That is true of the sentence and wrong about the job: an author looking at a
 * reference is asking where it came from, and an answer that is correct only sometimes is one
 * they cannot rely on without checking. The picker and the manager keep the short name — the
 * picker already groups by task, and the manager prints the address on its own line.
 */
export function aliasChipName(
  alias: AnswerRefAlias | undefined,
  src: NameSource,
  t: (k: string, o?: Record<string, unknown>) => string,
): string {
  const first = alias?.members[0];
  if (!alias || !first || !blockOf(src.tasks, first).block) {
    return alias?.name ?? t('blocks.answerRef.missingSource');
  }
  /*
   * A FAMILY NAME IS THE TEACHER'S, so it stands alone. The task prefix separates two blocks
   * that happen to share a name; a name that was chosen to be recognised needs no help.
   */
  const own = answerRefDefaultName(src, first, t, { omitTask: true });
  if (familyNameOf(first, src.aliases)) return own;
  const { task } = blockOf(src.tasks, first);
  return `${task?.title ?? t('blocks.answerRef.missingTask')} \u00b7 ${own}`;
}

/**
 * THE SHORT FORM, for a chip that has to fit somewhere narrow.
 *
 * `aliasChipName` spends the full address on an unnamed alias, which is right in prose — a
 * sentence has room, and "which answer is this" is the question a reader asks there. An
 * equation box is a single line a teacher is composing arithmetic in, and one chip reading
 * "Answer Recall Origin Test · Numerical 1 (full)" fills it end to end.
 *
 * So the same rule the picker's own rows follow: the block and facet WITHOUT the task. The
 * address is still one hover away.
 */
export function aliasShortName(
  alias: AnswerRefAlias | undefined,
  src: NameSource,
  t: (k: string, o?: Record<string, unknown>) => string,
  /** Which facets the surface offers — its suffix is worth less where it offers fewer. */
  kinds: 'any' | 'number' = 'any',
): string {
  const first = alias?.members[0];
  if (!alias || !first || !blockOf(src.tasks, first).block) {
    return alias?.name ?? t('blocks.answerRef.missingSource');
  }
  return answerRefDefaultName(src, first, t, { omitTask: true, kinds });
}

/**
 * Everything the chip and the manager need about one alias, or null outside authoring.
 *
 * `broken` covers both halves of "it is not there": every member's block or facet is gone, or
 * no route reaches any of them before here. They are one state because the teacher's next move
 * is the same — repoint it or remove it — and splitting them would be two reds meaning one
 * thing.
 */
export function useAnswerRefInfo(aliasId: string | null): AnswerRefInfo | null {
  const ctx = useContext(AnswerRefContext);
  const atExit = useAtExit();
  const { t } = useTranslation();
  return useMemo(() => {
    if (!ctx) return null;
    const alias = ctx.aliases.find(a => a.id === aliasId);
    /*
     * A chip naming no alias is BROKEN rather than absent.
     *
     * It is what a reference authored before aliases existed parses as, and what a chip pasted
     * without its alias becomes. Reporting it as broken puts it in the manager with a repoint
     * button, which is a migration path with no machinery behind it; rendering nothing would
     * lose the chip silently.
     */
    if (!alias) {
      const missing = t('blocks.answerRef.missingSource');
      return {
        status: 'broken' as const, name: missing, named: false, rawName: '',
        address: '', addresses: [], derived: missing,
      };
    }
    return {
      // The shared derivation, not a local restatement — the chip and the manager showing
      // different colours for one alias is the failure this import exists to prevent.
      // At the exit a same-task member resolves; in a block it cannot — see `AtExitContext`.
      // This is what makes a chip PASTED from an exit into a question paint itself broken.
      status: aliasStatus(alias, ctx.currentTaskId, ctx.tasks, { atExit }),
      // `useAnswerRefInfo` is the CHIP's hook, so its name is the chip's name.
      name: aliasChipName(alias, ctx, t),
      named: alias.name !== null && alias.name !== undefined,
      rawName: alias.name ?? '',
      /*
       * What the rename field offers to return to: the chip's spelling with the family name
       * set aside. `{ ...alias, name: null }` was how this was asked before and stopped
       * working the moment a name belonged to the family — the siblings still carry it, so
       * the derivation found it anyway and the placeholder echoed the name being cleared.
       */
      derived: aliasChipName(alias, { ...ctx, aliases: [] }, t),
      // One per member, because an alias with two addresses has two answers that count as the
      // same one, and a surface showing them wants them apart rather than run together.
      addresses: alias.members.map(m => answerRefAddress(ctx, m, t)),
      address: alias.members.map(m => answerRefAddress(ctx, m, t)).join(' / '),
    };
  }, [ctx, aliasId, t, atExit]);
}

/** Read the task list directly — the picker and the manager both enumerate it. */
export function useAnswerRefScope(): AnswerRefContextValue | null {
  return useContext(AnswerRefContext);
}

// ── Resolving chips for a reader ─────────────────────────────────────────────

/**
 * Turn a resolved answer into words, which is the half `@agavi/blocks` deliberately would not do.
 *
 * Every composite needs a connector that is translated, and the separator for a coordinate pair
 * is locale-dependent in a way nothing below the UI can know: pt-BR writes a decimal comma, so
 * "-38.3483, -38.3828" is ambiguous there. `Intl.ListFormat` handles the multi-select case for
 * the same reason — "A, B and C" is not a join with a comma.
 */
export function formatResolvedAnswer(
  r: ResolvedAnswer,
  t: (k: string, o?: Record<string, unknown>) => string,
  locale: string,
): string {
  switch (r.kind) {
    case 'scalar': return r.text;
    case 'range': return t('blocks.answerRef.range', { low: r.low, high: r.high });
    case 'pair': return t('blocks.answerRef.pair', { a: r.a, b: r.b });
    case 'measure': return t('blocks.answerRef.measure', { value: r.value, unit: r.unit });
    /* Two spellings rather than an empty `{{unit}}`, which leaves a trailing space that shows up
       in a chip and is read aloud as a pause. */
    case 'uncertain': return r.unit === undefined
      ? t('blocks.answerRef.uncertain', { value: r.value, uncertainty: r.uncertainty })
      : t('blocks.answerRef.uncertainMeasure', { value: r.value, uncertainty: r.uncertainty, unit: r.unit });
    // A scale point reads as both of its halves — "4 — Agree" — because either alone loses
    // what the teacher asked for when they chose the `full` facet.
    case 'point': return t('blocks.answerRef.point', { value: r.value, label: r.label });
    case 'list': return new Intl.ListFormat(locale, { style: 'long', type: 'conjunction' }).format(r.items);
    case 'empty': return '';
  }
}

/**
 * The chip's classes, owned here and imported by the node view.
 *
 * Both surfaces draw the same chip, and the only way to be sure of that is for there to be one
 * list. `globals.css` hangs the pill, the tint and the masked link mark off these.
 */
export const STATUS_CLASS: Record<AnswerRefStatus, string> = {
  ok: 'ws-answer-ref-chip',
  conditional: 'ws-answer-ref-chip ws-answer-ref-chip-conditional',
  broken: 'ws-answer-ref-chip ws-answer-ref-chip-broken',
};

const ESCAPES: Record<string, string> = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' };
const escapeHtml = (s: string) => s.replace(/[&<>"]/g, c => ESCAPES[c]!);

/**
 * Replace every answer-reference chip in a fragment with what the student actually answered.
 *
 * The reader's side of the chip: no name, no status, no iconography — just the value, reading as
 * part of the sentence it sits in. Returns the html unchanged when there is nothing to do, so
 * the common case costs one `includes`.
 *
 * ## What is escaped, and what is not
 *
 * A keyword or blank is text the STUDENT typed, so it is escaped — that is the one place student
 * input crosses into a rendered page. A chosen MCQ option is the TEACHER's own markup, recorded
 * resolved, and is inserted as-is so `H₂O` survives as `H₂O` rather than arriving as `H2O`.
 *
 * ## Unanswered shows the NAME, not a gap
 *
 * Only always-reachable sources can be inserted, so in a real run the answer is there. A teacher
 * jumping straight into a later task in Preview has not answered it yet, and SPR's version of
 * this rendered nothing at all — "In your previous question you said . Let's elaborate." Showing
 * the chip's own name says which answer is missing instead of leaving a hole in the sentence.
 */
/**
 * THE THIRD MODE, which answer references did not have and variables always did.
 *
 * A variable chip reads three ways — a pill on the canvas, a rolled value under test, the
 * student's value in Preview — and a reference only ever read two: the student's answer when
 * there was a session, and the chip itself when there was not. The editor's TEST card fell in
 * the second, so a teacher writing "your reading of {Soil pH} suggests..." could test the
 * block and never once see the sentence they were writing. The chip stayed a chip, and the
 * one surface built for reading a block as a student reads it was the one surface that could
 * not.
 *
 * `rolled` is the same signal the variable chips use and carries the same meaning: `null` is
 * display mode and leaves a reference as a chip; a record is a test surface and resolves it.
 * Passed in rather than read from `useBlockPreview`, because this module must not import the
 * one that holds it — that module imports this one.
 *
 * Under test, with no student to have answered, the reference resolves to a SAMPLE: what a
 * correct answer to the source block would be, given this roll. Marked as a sample in the
 * markup so nothing can mistake it for a run, and absent whenever there is no correct answer
 * to take — a survey, an unfinished key, a formula this roll cannot evaluate — in which case
 * it falls back to the chip, exactly as before.
 */
/**
 * The first member of an alias that has a correct answer to stand in for, already resolved.
 *
 * `trusted` rides along because MCQ and Dropdown record an option's own markup and everything
 * else records text a student typed — the same split the session path makes when it decides
 * whether to escape.
 */
/**
 * AN ANSWER QUOTED MID-SENTENCE HAS TO BE INLINE.
 *
 * MCQ and Dropdown record the option's own markup, which is rich text and therefore wrapped in
 * a `<p>`. Dropped into prose as stored, it makes the quoted answer its own paragraph: a line
 * break, the word, another line break, in the middle of a sentence written to flow around it.
 *
 * Not a sample-only problem — a real student's recorded answer is the same stored markup and
 * broke the same way, which is why this unwraps in the one place both paths meet. Block
 * wrappers are peeled and their contents joined by a space; everything inside them (an
 * emphasis, a colour a teacher chose, a subscript) is kept, because that is the answer as the
 * student saw it.
 */
function inlineHtml(html: string): string {
  const holder = document.createElement('div');
  holder.innerHTML = html;
  const BLOCK = 'p, div, h1, h2, h3, h4, h5, h6, li, ul, ol, blockquote';
  // Innermost first, so a list inside a paragraph unwraps cleanly rather than stranding its
  // children inside a wrapper that is itself about to go.
  let node = holder.querySelector(BLOCK);
  while (node) {
    const parts = Array.from(node.childNodes);
    node.replaceWith(...(parts.length > 0 ? parts : []), document.createTextNode(' '));
    node = holder.querySelector(BLOCK);
  }
  return holder.innerHTML.replace(/\s+/g, ' ').trim();
}

/**
 * WHAT THE SOURCE BLOCK SAYS ABOUT THE ANSWER ON SCREEN.
 *
 * The same three the app marks feedback with — `fired-feedback` writes exactly these — so a
 * teacher meets one vocabulary for "this is right" and not two. Text rather than an icon
 * because this is composed into an html string, and a glyph set in the running text cannot
 * disturb the line height of the sentence around it.
 */
export const VERDICT_GLYPH: Record<string, string> = {
  correct: '\u2713',
  incorrect: '\u2717',
  neutral: '\u2013',
  open: '\u2013',
};

/**
 * WHICH OF THE FIVE SAMPLES IS SHOWING, when the verdict alone does not say.
 *
 * Two tiers are correct and two are incorrect, so `✓`/`✗` cannot carry the distinction. The
 * marks here extend that vocabulary rather than replacing it: a teacher who has learned the
 * tick still reads a tick.
 *
 *   exact    the answer the teacher typed  — a ✓ with a ring, drawn in CSS
 *   correct  another accepted value        — a plain ✓
 *   near     just outside the band         — a plain ✗
 *   mistake  clearly outside               — ✗✗, doubling for degree
 *
 * `+`/`−` suffixes were the other candidate and are worse twice over: `−` already means
 * neutral here, and the two distinctions are not the same KIND. Exact-versus-correct is not
 * "more correct" — 9.84 is as accepted as 10 — it is the authored answer against any other,
 * which is a difference in identity, not degree. A ring says "this one" without implying rank.
 *
 * The ring is CSS and not a codepoint on purpose: no circled check renders dependably across
 * the fonts this app ships, and a glyph that falls back to a box is worse than no mark.
 */
export const TIER_MARK: Record<string, { glyph: string; ringed?: boolean }> = {
  exact: { glyph: '\u2713', ringed: true },
  correct: { glyph: '\u2713' },
  near: { glyph: '\u2717' },
  mistake: { glyph: '\u2717\u2717' },
};

/**
 * The tier's NAME, spelled out as five literal keys rather than built from the tier.
 *
 * `t(\`...tier.${tier}\`)` would have been shorter and is the thing `invariants.test.ts` caps at
 * fifty across the app, for the reason that cap exists: a key assembled at runtime cannot be
 * found by searching for it, so a translator removing one gets no warning and the string
 * silently renders as its own key. Written out, every one of these greps.
 */
const TIER_NAME_KEY: Record<string, string> = {
  exact: 'blocks.answerRef.tierExact',
  correct: 'blocks.answerRef.tierCorrect',
  near: 'blocks.answerRef.tierNear',
  mistake: 'blocks.answerRef.tierMistake',
  open: 'blocks.answerRef.tierOpen',
};

/**
 * The roll one recalled answer is worked out against: its OWN task's, never the viewing task's.
 *
 * Every caller here wants this and none of them wants a flat map, so the lookup lives in one
 * place rather than at six call sites each remembering to pass the right task.
 */
function benchFor(alias: AnswerRefAlias, ctx: AnswerRefContextValue): Record<string, number> {
  const taskId = firstLiveMember(alias, ctx)?.member.taskId;
  return (taskId ? ctx.benchVariablesFor?.(taskId) : undefined) ?? {};
}

/** One alias's first live address, which is what a stand-in is understood to answer. */
function firstLiveMember(alias: AnswerRefAlias, ctx: AnswerRefContextValue) {
  for (const member of alias.members) {
    const task = ctx.tasks.find(tk => tk.id === member.taskId);
    const block = task?.blocks?.find(b => b.id === member.blockId);
    if (block) return { member, block };
  }
  return null;
}

/**
 * A STAND-IN FOR A REFERENCE THE RUN HAS NOT REACHED, as the sentence will read it.
 *
 * Through the same resolver and the same formatter a walked answer goes through, so the
 * sentence a teacher checks is the sentence a student gets — a second rendering path here
 * would be a second chance to disagree about how an answer reads.
 *
 * The alias's FIRST LIVE member is the address, matching what the editor's bench stands in for:
 * a multi-member alias is the same answer reached different ways, so any of them names it.
 */
function standInText(
  alias: AnswerRefAlias,
  ctx: AnswerRefContextValue,
  answer: unknown,
  formatFor: (type: string | undefined, facet: string) => AnswerFormat,
  t: (k: string, o?: Record<string, unknown>) => string,
  locale: string,
  uncertaintyFormat?: AnswerFormat | null,
): string {
  const hit = firstLiveMember(alias, ctx);
  if (!hit) return '';
  const data = (hit.block.data ?? {}) as Record<string, unknown>;
  const resolved = resolveAnswerRef(hit.member, answer, hit.block.type, data);
  if (resolved.kind === 'empty') return '';
  return formatResolvedAnswer(
    applyAnswerFormat(
      resolved, formatFor(hit.block.type, hit.member.facet), hit.member.facet, uncertaintyFormat),
    t, locale,
  );
}

type Sampled = ResolvedAnswer & {
  trusted: boolean; cyclable: number; verdict: AnswerChoice['kind']; tier?: AnswerTier;
};

/**
 * ONE ANSWER, RESOLVED AND JUDGED — the shape every surface that shows a stand-in reads.
 *
 * Takes the block and the facet rather than an alias, because the recall row's DRIVER entry
 * has no alias to take: a block-address driver is a source with no name. Everything else is
 * unchanged, and `resolvedFor` is the alias-shaped wrapper it always was.
 */
function resolvedAt(
  hit: { block: Block; member: AnswerRef },
  answer: unknown,
  variables: Record<string, number>,
  /** The source's answers, where the caller has already enumerated them — see below. */
  known?: AnswerChoice[],
): Sampled | null {
  const data = (hit.block.data ?? {}) as Record<string, unknown>;
  const resolved = resolveAnswerRef(hit.member, answer, hit.block.type, data);
  if (resolved.kind === 'empty') return null;
  /* `answerChoicesFor` walks a block's whole answer space and resolves its branches, and the
     row's caller has just done it to build the cycle — so it is handed in rather than done
     twice per entry per render. */
  const choices = known ?? answerChoicesFor(hit.block.type, data, { variables, facet: hit.member.facet });
  const cyclable = choices.filter(c => c.sample !== null).length;
  // Which answer this IS, so the sentence can say so — see `VERDICT_GLYPH`.
  const showing = choices.find(c => JSON.stringify(c.sample) === JSON.stringify(answer));
  const verdict = showing?.kind ?? 'neutral';
  return {
    ...resolved, trusted: hit.block.type === 'mcq' || hit.block.type === 'dropdown', cyclable, verdict,
    tier: showing?.tier,
  };
}

/** A stand-in the teacher chose, rendered through the one renderer a real answer goes through. */
function resolvedFor(
  alias: AnswerRefAlias,
  ctx: AnswerRefContextValue,
  answer: unknown,
  variables: Record<string, number>,
): Sampled | null {
  const hit = firstLiveMember(alias, ctx);
  return hit ? resolvedAt(hit, answer, variables) : null;
}

function sampleForAlias(
  alias: AnswerRefAlias,
  ctx: AnswerRefContextValue,
  variables: Record<string, number>,
): (ResolvedAnswer & { trusted: boolean; cyclable: number; verdict: AnswerChoice['kind']; tier?: AnswerTier }) | null {
  for (const member of alias.members) {
    const block = ctx.tasks.find(tk => tk.id === member.taskId)?.blocks?.find(b => b.id === member.blockId);
    if (!block) continue;
    const data = (block.data ?? {}) as Record<string, unknown>;
    const sample = sampleCorrectAnswer(block.type, data, { variables });
    // No separate null check: `resolveAnswerRef` reads a null answer as empty, which is the
    // same "nothing to show here" the next line already acts on.
    const resolved = resolveAnswerRef(member, sample, block.type, data);
    if (resolved.kind === 'empty') continue;
    /*
     * HOW MANY ANSWERS THERE ARE TO CYCLE THROUGH, which decides whether the value is a
     * control at all. A GPS fix or a slider position has exactly one answer this surface can
     * offer, and a click would set the value it already has: an affordance that looks live
     * and does nothing, which `IssueRow` refuses to render for the same reason.
     */
    const choices = answerChoicesFor(block.type, data, { variables, facet: member.facet });
    const cyclable = choices.filter(c => c.sample !== null).length;
    // What the sampler produces is the source's correct answer, every time.
    return {
      ...resolved, trusted: block.type === 'mcq' || block.type === 'dropdown',
      // LOOKED UP, not assumed. Hardcoding `exact` here gave a keyword block a ringed tick —
      // a mark meaning "the authored value, as against another accepted one" on a block that
      // has no such distinction to draw. Only the tolerance blocks carry tiers; everything
      // else leaves it undefined and keeps the plain verdict glyph.
      cyclable, verdict: 'correct' as const,
      tier: choices.find(c => JSON.stringify(c.sample) === JSON.stringify(sample))?.tier,
    };
  }
  return null;
}

/**
 * EVERY RECALLED ANSWER THIS SURFACE CAN PUT A NUMBER TO, keyed by alias id.
 *
 * The map `evaluateFormulaRaw` takes, merged alongside the variable roll — which is why a
 * formula holding `{speed}` and `[their answer]` needs no special casing anywhere downstream:
 * both arrive as entries in one `Record<string, string>`, and the rewrite in `preprocessFormula`
 * turns each token into the symbol its value is registered under.
 *
 * ## It follows the chip's precedence, deliberately
 *
 * Running: the student's own answer from the first member they actually reached, then the
 * teacher's stand-in, then nothing. Authoring or testing: the sampled answer. That is exactly
 * what `useAnswerRefHtml` does for the sentence, and it has to be, or the chip in the question
 * would say one number while the equation grading it computed another — the editor-disagrees-
 * with-grader fault this codebase keeps rediscovering. `recall-agreement.test.tsx` pins the two
 * together rather than trusting this comment.
 *
 * ## An alias that resolves to no NUMBER is simply absent
 *
 * Not zero. A missing entry leaves an unknown symbol, which makes the whole formula NaN and the
 * answer unresolved — the honest outcome. A zero would quietly grade, and `0` is a plausible
 * enough answer that nobody would question it.
 */
export function useRecallFormulaValues(rolled: Record<string, string> | null): Record<string, string> {
  const ctx = useContext(AnswerRefContext);
  const session = useRunSession();
  const { standIns } = useRecallStandIns();
  /* A formula in exit text reads from the same moment the prose around it does — see
     `RecallAsOfContext`. One sentence quoting two different instants would be worse than either. */
  const asOf = useContext(RecallAsOfContext) ?? undefined;
  /* `atExit` travels with `asOf` and for the same reason: a same-task member resolves at
     the exit and nowhere else, so a resolver that does not know where it is skips it as
     dead and the chip prints its own address instead of the answer. */
  const atExit = useAtExit();
  const underTest = rolled !== null;

  return useMemo(() => {
    const out: Record<string, string> = {};
    if (!ctx) return out;

    for (const alias of ctx.aliases) {
      const bench = benchFor(alias, ctx);
      /*
       * THE CYCLED VALUE OUTRANKS THE SAMPLE, exactly as it does in the sentence.
       *
       * `testAnswers` is where the chip's cycle puts what the teacher chose, and the display
       * path has always preferred it. Reading only the sample here meant cycling a chip to a
       * near miss changed the number in the sentence and NOT the number the equation computed
       * with — the two readings of one answer disagreeing, which is the whole thing
       * `recall-agreement.test.tsx` exists to stop and did not, because it only ever exercised
       * the default.
       */
      const chosen = underTest ? ctx.testAnswers?.[alias.id] : undefined;
      const n = underTest
        ? numberFrom(chosen !== undefined
          ? resolvedFor(alias, ctx, chosen, bench)
          : sampleForAlias(alias, ctx, bench))
        : runningNumberFor(alias, ctx, session, standIns, asOf, { atExit });
      if (n !== null) out[alias.id] = String(n);
    }
    return out;
  }, [ctx, session, standIns, underTest, asOf, atExit]);
}

/** The running precedence: the answer the student gave, then the teacher's stand-in. */
function runningNumberFor(
  alias: AnswerRefAlias,
  ctx: AnswerRefContextValue,
  session: ReturnType<typeof useRunSession>,
  standIns: Record<string, unknown>,
  /** See `RecallAsOfContext` — the moment this sentence speaks from, or undefined for now. */
  asOf?: string,
  /** See `ReachSite` — a same-task member resolves at the exit and nowhere else. A parameter
   *  rather than a hook because this is a plain function; its caller reads the context. */
  site?: ReachSite,
): number | null {
  if (!session) return null;
  const member = resolveAlias(alias, ctx.currentTaskId, ctx.tasks,
    m => lastAnswersFor(session, m.taskId, 'session', asOf)[m.blockId] !== undefined,
    site);
  if (member) {
    const block = ctx.tasks.find(tk => tk.id === member.taskId)?.blocks?.find(b => b.id === member.blockId);
    if (block) {
      const answer = lastAnswersFor(session, member.taskId, 'session', asOf)[member.blockId];
      const n = resolvedAnswerNumber(
        resolveAnswerRef(member, answer, block.type, (block.data ?? {}) as Record<string, unknown>));
      if (n !== null) return n;
    }
  }
  // Only once nothing walked to it, exactly as the sentence does — a real answer always wins.
  const standIn = standIns[alias.id];
  if (standIn === undefined) return null;
  const hit = firstLiveMember(alias, ctx);
  if (!hit) return null;
  return resolvedAnswerNumber(
    resolveAnswerRef(hit.member, standIn, hit.block.type, (hit.block.data ?? {}) as Record<string, unknown>));
}

const numberFrom = (r: ResolvedAnswer | null): number | null =>
  r === null ? null : resolvedAnswerNumber(r);

/**
 * THE VARIABLE ROLL WITH THE RECALLED ANSWERS IN IT.
 *
 * One map, because nothing downstream should have to know which kind of thing it is holding.
 * `evaluateFormulaRaw` takes a `Record<string, string>` and `preprocessFormula` turns `{name}`
 * and `[ara_...]` each into the symbol its value is registered under — so an equation mixing a
 * variable and a recalled answer resolves through the path that already existed, and the
 * eighty-odd call sites that merely RECEIVE this map need no change at all.
 *
 * Keys cannot collide: a variable name must start with a letter (the panel enforces
 * `/^[a-zA-Z][a-zA-Z0-9_]*$/`) and an alias id starts `ara_`... which also starts with a
 * letter. They are distinguished by the TOKEN that reads them, not by the key — a `{name}`
 * only ever resolves a variable and a `[id]` only ever resolves an alias, because the rewrite
 * sends the second through `formulaRefKey` and the first does not.
 *
 * Call it where a block computes its roll, so the equation field, the answer bar and the
 * issue list all read one map rather than three that have to be kept in step.
 */
export function useRolledWithRecalls(rolled: Record<string, string>): Record<string, string> {
  const recalls = useRecallFormulaValues(rolled);
  return useMemo(
    () => (Object.keys(recalls).length === 0 ? rolled : { ...rolled, ...recalls }),
    [rolled, recalls],
  );
}

export function useAnswerRefHtml(
  /*
   * REQUIRED, with no default, and that is the whole point of the signature.
   *
   * It defaulted to `null` for one afternoon, which was long enough for three of the four
   * callers to take it: Text, FITB and the fired-feedback boxes each render stored html
   * themselves rather than through `PreviewHtml`, each already had the roll in hand, and each
   * called this with nothing and got the authoring behaviour for ever. A reference in prose —
   * which is where a reference is most naturally written — could not be read as a sentence on
   * any surface. A default here is an answer the compiler supplies on the caller's behalf to a
   * question only the caller can answer.
   */
  rolled: Record<string, string> | null,
): (html: string) => string {
  const ctx = useContext(AnswerRefContext);
  const session = useRunSession();
  // `?? undefined`, because the context's "now" is null and the reader's is undefined — see
  // `RecallAsOfContext`.
  const asOf = useContext(RecallAsOfContext) ?? undefined;
  /* `atExit` travels with `asOf` and for the same reason: a same-task member resolves at
     the exit and nowhere else, so a resolver that does not know where it is skips it as
     dead and the chip prints its own address instead of the answer. */
  const atExit = useAtExit();
  // The teacher's own answers for references this run has not reached — see the pending branch.
  const { standIns, set: setStandIn } = useRecallStandIns();
  const canSetStandIn = !!setStandIn;
  const { t, i18n } = useTranslation();

  /*
   * ONLY THE MODE, not the numbers. `rolled` answers "is this a test surface", which is the
   * one thing the caller knows and this does not; the numbers a sample is worked out from
   * come from `benchVariables`, so every surface agrees about them — see its own note.
   */
  const underTest = rolled !== null;

  return useMemo(() => (html: string) => {
    if (!html || !html.includes('data-answer-ref-')) return html;
    const div = document.createElement('div');
    div.innerHTML = html;

    div.querySelectorAll<HTMLElement>('[data-answer-ref-alias], [data-answer-ref-block]').forEach(el => {
      const aliasId = el.getAttribute('data-answer-ref-alias') ?? '';
      const alias = ctx?.aliases.find(a => a.id === aliasId);
      /*
       * HOW THIS OCCURRENCE READS. Carried by the chip rather than the alias, so two chips
       * quoting one answer can read two ways — see the attribute's own note.
       *
       * ABSENT MEANS THE SOURCE'S OWN DEFAULT, which is what the menu shows selected on a
       * chip nobody has set: `degrees` on a bearing, `decimal` on a coordinate. Defaulting
       * to `asWritten` here instead reads identically today, because each of those is its
       * domain's identity — and states the rule a second time, free to drift from the one
       * the menu asks.
       */
      const stored = el.getAttribute('data-answer-ref-format') as AnswerFormat | null;
      const formatFor = (type: string | undefined, facet: string) =>
        stored ?? (type ? defaultFormatFor(type, facet) : 'asWritten');
      /* The uncertainty's own count, where the chip carries one. No default: absent means the
         value's count covers both halves, which is how every chip read before it existed. */
      const storedUncertainty =
        el.getAttribute('data-answer-ref-uncertainty-format') as AnswerFormat | null;
      const facetOf = (a: AnswerRefAlias) => a.members[0]?.facet ?? '';

      /*
       * AUTHORING: redraw the chip with the alias's CURRENT name and status.
       *
       * This is the half that makes the canvas card and the editor identical by construction
       * rather than by both being kept in step. The stored html carries a baked name so a
       * renderer with no context has something to print; here there IS context, so the baked
       * copy is overwritten with the live one and renaming an alias updates every surface at
       * once instead of leaving the un-edited blocks showing the old name.
       *
       * A chip whose alias is missing — authored before aliases, or pasted without one — is
       * drawn broken with whatever name it carries, which is visible and repointable rather
       * than silently gone.
       */
      if (!session) {
        if (!ctx) return;

        /*
         * UNDER TEST: stand a sample in for the answer nobody has given.
         *
         * The member is chosen the way the session path chooses one — the first this task can
         * reach — except that "reachable" here means "has a correct answer to sample" rather
         * than "the student answered it".
         */
        if (underTest && alias) {
          // The teacher's own stand-in outranks the sample — that is what setting one means.
          const chosen = ctx.testAnswers?.[alias.id];
          const bench = benchFor(alias, ctx);
          const sampled = chosen !== undefined
            ? resolvedFor(alias, ctx, chosen, bench)
            : sampleForAlias(alias, ctx, bench);
          if (sampled) {
            /*
             * A BUTTON, because the value is also the control.
             *
             * Clicking it takes the next answer the source block could give — two options is
             * the common case, and one click is the whole gesture. It was a span for a while,
             * because this value lands in prose that carried `role="button"` and a real
             * button nested in one is invalid; that role is gone from a selected card now, so
             * the control can be the thing it is: focusable, operable by keyboard, announced
             * as a control rather than as stray words in a sentence.
             *
             * A plain span where there is nowhere to cycle to — a GPS fix has one answer this
             * surface can produce, and a control that does nothing is worse than none.
             *
             * `data-answer-ref-pick` rather than a React handler: this is an html STRING
             * written into the page, so the click is caught by one delegated listener on the
             * card. The alias id rides along because the card has no other way to know which
             * of several references was pressed.
             */
            const pickable = !!ctx.setTestAnswer && sampled.cyclable > 1;
            const el2 = document.createElement(pickable ? 'button' : 'span');
            el2.className = 'ws-answer-ref-sample';
            if (pickable) {
              (el2 as HTMLButtonElement).type = 'button';
              el2.setAttribute('data-answer-ref-pick', alias.id);
              el2.setAttribute('aria-label', t('blocks.answerRef.simulate.pickA11y', {
                name: aliasDisplayName(alias, ctx, t),
              }));
            }
            const shown = applyAnswerFormat(
              sampled, formatFor(firstLiveMember(alias, ctx)?.block.type, facetOf(alias)),
              facetOf(alias), storedUncertainty,
            );
            const text = formatResolvedAnswer(shown, t, i18n.language);
            el2.innerHTML = sampled.trusted ? inlineHtml(text) : escapeHtml(text);

            /*
             * AND THE VERDICT BESIDE IT.
             *
             * Cycling walks answers across the whole correctness spectrum and a value alone
             * does not say which one is on screen — most visibly on a Numerical, where every
             * answer is a bare number. The mark rides WITH the value rather than sitting in a
             * panel elsewhere on the card, because it answers a question about this word.
             *
             * `aria-hidden`: the control's own label already names the reference, and a
             * screen reader meeting a stray tick in the middle of a sentence learns nothing.
             */
            const mark = document.createElement('span');
            const tierMark = sampled.tier ? TIER_MARK[sampled.tier] : undefined;
            mark.className = tierMark?.ringed
              ? 'ws-answer-ref-verdict ws-answer-ref-verdict-ringed'
              : 'ws-answer-ref-verdict';
            mark.setAttribute('data-verdict', sampled.verdict);
            if (sampled.tier) mark.setAttribute('data-tier', sampled.tier);
            mark.setAttribute('aria-hidden', 'true');
            mark.textContent = tierMark?.glyph ?? VERDICT_GLYPH[sampled.verdict] ?? VERDICT_GLYPH.neutral!;
            el2.append(mark);

            /*
             * THE TIER IN THE NAME, because the mark is `aria-hidden` and the number alone
             * does not say which sample it is — a sighted teacher compares it against the
             * answer they wrote, and a screen-reader user cannot. The control is already
             * named; this appends which of the five it is currently showing.
             */
            if (sampled.tier && pickable) {
              const current = t(TIER_NAME_KEY[sampled.tier] ?? '');
              el2.setAttribute('aria-label', `${el2.getAttribute('aria-label') ?? ''} ${current}`.trim());
            }

            el.replaceWith(el2);
            return;
          }

          /*
           * NOTHING TO SAMPLE, SAID OUT LOUD — because a chip that simply does not resolve
           * reads as a bug.
           *
           * The source is there and its answer key is empty: a question the teacher has not
           * finished. Every reference around it reads as a value, so leaving this one as a
           * chip makes the teacher look for a fault in the chip. Reported as "a chip that
           * simply doesn't render looks like a bug".
           *
           * Only where the SOURCE RESOLVES. A reference whose block is gone is a broken
           * reference, and the chip's own broken styling below is the right thing for it —
           * "no answer set" would be a lie about a block that is not there to have one.
           */
          if (firstLiveMember(alias, ctx)) {
            /*
             * AND IT STAYS A CONTROL WHERE THE TEACHER CYCLED HERE, or the cycle dies on it.
             *
             * `chosen !== undefined` means this is a value the teacher stepped to, not the
             * resting sample. Rendering a plain span there removed the only thing the pick
             * handler looks for — `data-answer-ref-pick` — so the next click found nothing
             * and the chip could never come back. Reported as "it cycles to 'No Answer Set'
             * ... and then gets stuck there".
             *
             * A placeholder is a legitimate stop on the way round; it is a dead end only if
             * it stops being pressable.
             */
            const stepped = chosen !== undefined;
            const unset = document.createElement(stepped ? 'button' : 'span');
            unset.className = 'ws-answer-ref-sample ws-answer-ref-unset';
            if (stepped) {
              (unset as HTMLButtonElement).type = 'button';
              unset.setAttribute('data-answer-ref-pick', alias.id);
              unset.setAttribute('aria-label', t('blocks.answerRef.simulate.pickA11y', {
                name: aliasDisplayName(alias, ctx, t),
              }));
            }
            unset.textContent = t('blocks.answerRef.simulate.noAnswerSet');
            el.replaceWith(unset);
            return;
          }
        }

        const span = document.createElement('span');
        span.className = alias
          ? STATUS_CLASS[aliasStatus(alias, ctx.currentTaskId, ctx.tasks, { atExit })]
          : STATUS_CLASS.broken;
        // The chip's own spelling, not the short one: a display block's chip is read by an
        // author asking which answer it quotes, exactly as the editor's is.
        span.textContent = alias
          ? aliasChipName(alias, ctx, t)
          : el.getAttribute('data-answer-ref-fallback') ?? el.textContent ?? '';
        el.replaceWith(span);
        return;
      }

      // RUNNING: the student's own answer, from the first member they actually reached.
      const member = alias && ctx
        ? resolveAlias(alias, ctx.currentTaskId, ctx.tasks,
          m => lastAnswersFor(session, m.taskId, 'session', asOf)[m.blockId] !== undefined,
          { atExit })
        : null;
      const block = member
        ? ctx?.tasks.find(tk => tk.id === member.taskId)?.blocks?.find(b => b.id === member.blockId)
        : undefined;
      const answer = member ? lastAnswersFor(session, member.taskId, 'session', asOf)[member.blockId] : undefined;
      const resolved = member && block
        ? resolveAnswerRef(member, answer, block.type, (block.data ?? {}) as Record<string, unknown>)
        : ({ kind: 'empty' } as ResolvedAnswer);
      // The student's own answer reads the way the chip asks for too — the format is about
      // the sentence, not about where the answer came from.
      const text = formatResolvedAnswer(
        applyAnswerFormat(
          resolved, formatFor(block?.type, member?.facet ?? ''), member?.facet ?? '',
          storedUncertainty),
        t, i18n.language,
      );

      if (text === '') {
        /*
         * NOTHING WALKED TO IT YET, so the teacher's own stand-in answers instead.
         *
         * The second of the two ways to fill a reference under Preview — the first is simply
         * answering the source, which is the branch above. Consulted only HERE, which is what
         * makes a real answer always win: walk the source and this code never runs.
         */
        const standIn = alias ? standIns[alias.id] : undefined;
        const stood = standIn !== undefined && alias && ctx
          ? standInText(alias, ctx, standIn, formatFor, t, i18n.language, storedUncertainty)
          : '';
        if (stood !== '') {
          const span = document.createElement('span');
          /*
           * NOT TEACHER FURNITURE. The value is the sentence: a student meets this question
           * with their own earlier answer in it, and a stand-in is standing in for exactly
           * that, so the read-through has to read it.
           *
           * It was marked `data-teacher-only` to keep a dotted underline out of that report,
           * on the stated theory that "the WORDS are the sentence and stay". They did not:
           * the scan's `isHidden` skips the whole SUBTREE of anything so marked, so the value
           * was being deleted from the sentence a teacher was asking to hear. The underline is
           * gone now -- see the stylesheet for why -- and with no furniture left on this span
           * there is nothing to hide, so the door closes.
           */
          span.className = 'ws-answer-ref-standin';
          if (canSetStandIn && !ctx?.bandedAliasIds?.has(alias!.id)) {
          span.setAttribute('data-answer-ref-standin', alias!.id);
        }
          span.innerHTML = inlineHtml(stood);
          el.replaceWith(span);
          return;
        }
        /*
         * Nothing resolved. The alias's FALLBACK is the authored answer to this, and it is why
         * a fallback exists at all — a multi-member alias spans paths, so some path reaches
         * none of its members. With no fallback the chip's name is shown dimmed, which says
         * WHICH answer is missing rather than leaving a hole in the sentence the way SPR did.
         */
        const placeholder = document.createElement('span');
        placeholder.className = 'ws-answer-ref-pending';
        // And it is the control for setting one — see `useStandInPickHandler`. Teacher-only for
        // the same reason the stand-in itself is: a student meets the sentence, not the bench.
        if (canSetStandIn && alias && !ctx?.bandedAliasIds?.has(alias.id)) {
          placeholder.setAttribute('data-answer-ref-standin', alias.id);
          placeholder.setAttribute('data-teacher-only', '');
        }
        placeholder.textContent = alias?.fallback
          || (ctx ? aliasDisplayName(alias, ctx, t) : el.textContent ?? '');
        el.replaceWith(placeholder);
        return;
      }
      // Teacher markup for a chosen option, escaped student text for everything else.
      const trusted = block?.type === 'mcq' || block?.type === 'dropdown';
      const span = document.createElement('span');
      span.innerHTML = trusted ? inlineHtml(text) : escapeHtml(text);
      el.replaceWith(...Array.from(span.childNodes));
    });
    return div.innerHTML;
    /* `atExit` AND `asOf` belong here: both change what a chip resolves to, and a memo that
       does not list them hands back the previous answer. `asOf` was already missing — the
       same bug, waiting for a surface that changes it without changing anything else. */
  }, [ctx, session, underTest, standIns, canSetStandIn, t, i18n.language, atExit, asOf]);
}


// ── Reporting a dead reference on the block that holds it ───────────────────

/**
 * The broken references inside one block, as block issues.
 *
 * The References panel already lists these per alias, which answers "what is broken in this
 * task". It does not answer the question a teacher actually arrives with, which is "is this
 * block finished" — and that is asked of the block, in its own issues panel, next to every
 * other reason it is not ready. A reference that resolves to nothing is a defect by the
 * `BlockIssue` definition: both sides exist, more typing will not fix it, and what the student
 * sees is a sentence with a hole in it.
 *
 * `defect`, not `incompletion`: an absence is a block still being written, and this is a
 * CONTRADICTION between two things the teacher authored — a chip here and a block that is no
 * longer reachable, or no longer there at all.
 *
 * Returns nothing outside authoring, and nothing for a healthy or merely conditional
 * reference. `conditional` carries no warning anywhere, for the reason `AnswerRefReach` gives.
 */
export function useBrokenRefIssues(blockId: string | undefined): BlockIssue[] {
  const ctx = useContext(AnswerRefContext);
  const { t } = useTranslation();
  /* The decision lives in `lib/recall-issues` so the BOARD can ask it too. It could not before:
     these were hooks and nothing else, so a dead reference was drawn on the card and invisible
     to the error panel and to the publication gate, which is an error that does not hold. */
  return useMemo(
    () => (ctx ? brokenRefIssues(blockId, { ...ctx, t } as never) : []),
    [ctx, blockId, t],
  );
}

/**
 * A RECALL IN A CALCULATION THAT SOME STUDENTS NEVER ANSWER.
 *
 * `conditional` carries no warning anywhere else, deliberately and rightly: a reference to a
 * pathway answer is as often legitimate as not, and in PROSE it degrades — the chip shows its
 * fallback, or nothing, and the sentence still reads.
 *
 * ARITHMETIC HAS NO FALLBACK. `evaluateEquation` returns null when a recall has no value
 * rather than guessing, which is the right contract and pinned as such in
 * `one-formula-rewrite.test.ts`. So for a student who took the other route the answer key is
 * simply UNSET — silently, with nothing on the block to say so and nothing in the gate to
 * hold it. The guard is one-sided today: KIND is enforced for equations, so a text answer
 * cannot enter one, and reach is not.
 *
 * `inFormula` rather than a classification, because it is already exactly the question — a
 * formula field stores `[ara_…]` where prose stores a chip span. That covers an answer
 * equation and a branch condition together, which is right: a condition that cannot resolve
 * takes a branch nobody authored, and it fails as quietly as the key does.
 *
 * A DEFECT and not an incompletion. Nothing here is unfinished; it is wrong, for a knowable
 * share of the class, and the repair is real — alias the sources together until the cover is
 * total, or move the driver onto the spine.
 */
export function useConditionalFormulaRefIssues(blockId: string | undefined): BlockIssue[] {
  const ctx = useContext(AnswerRefContext);
  const { t } = useTranslation();
  // Same arrangement, same reason — see `useBrokenRefIssues` above.
  return useMemo(
    () => (ctx ? conditionalFormulaRefIssues(blockId, { ...ctx, t } as never) : []),
    [ctx, blockId, t],
  );
}

/**
 * THE WARNING ON THE BLOCK BEING REFERENCED, rather than on the one doing the referencing.
 *
 * `useBrokenRefIssues` above reports where a chip SITS. That is the right place to repair a
 * reference and the wrong place to learn you have just broken one: make a referenced block
 * optional, or give one of its options an image, and the only warning appears on a task you
 * may never open. The teacher's own words for the panel version of this problem — "you can't
 * tell from the collapsed card" — are the same complaint one surface along.
 *
 * So: a block that something recalls, which can no longer be read, says so where the teacher
 * just made the change. It was a locked decision in the original scope ("switching a block to
 * optional warns if references exist") that never got built.
 *
 * A WARNING, not an error. Nothing about this block is wrong — it is a legitimate block in a
 * legitimate state, and what it costs is somewhere else. The error belongs to the reference,
 * and is already raised there.
 */
export function useRecallBlockedHere(blockId: string | undefined):
  { reason: string; chips: number; firstUsage?: { taskId: string; blockId: string } } | null {
  const ctx = useContext(AnswerRefContext);
  return useMemo(() => {
    if (!ctx || !blockId) return null;

    // Which names point AT this block. One alias can hold several addresses, and only the
    // ones naming this block matter.
    const naming = ctx.aliases.filter(a => a.members.some(m => m.blockId === blockId));
    if (naming.length === 0) return null;

    const block = ctx.tasks.flatMap(tk => tk.blocks ?? []).find(b => b.id === blockId);
    if (!block) return null;
    const eligibility = answerRefEligibility(block.type, (block.data ?? {}) as Record<string, unknown>);
    if (eligibility.referenceable) return null;

    /*
     * Counted in BLOCKS, which is the teacher's own unit — "another block already references
     * it". Two chips in one block is still one place to go and fix, and the names they are
     * organised under are the panel's business rather than this warning's.
     */
    const ids = new Set(naming.map(a => a.id));
    const usages = collectAliasUsages(ctx.tasks as never).filter(u => ids.has(u.aliasId));
    const blocks = new Set(usages.map(u => u.inBlockId));
    if (blocks.size === 0) return null;
    /*
     * The FIRST usage in experience order is where the warning's link goes. Several blocks can
     * recall the same answer and the count says so, but a link has to land somewhere, and the
     * earliest one is the only choice that does not need the teacher to have a reason.
     */
    const first = usages[0]!;
    return {
      reason: eligibility.reason,
      chips: blocks.size,
      firstUsage: { taskId: first.inTaskId, blockId: first.inBlockId },
    };
  }, [ctx, blockId]);
}

/**
 * The same fact as a block issue, for the panel that lists every problem a block has.
 *
 * Split from the hook above because the CONTROL that caused it wants to mark itself, and it
 * needs the bare reason to know whether it was the culprit — a grading-mode button should not
 * wear a warning about an image somebody put on an option.
 */
export function useReferencedSourceIssues(blockId: string | undefined): BlockIssue[] {
  const blocked = useRecallBlockedHere(blockId);
  const openUsage = useContext(AnswerRefContext)?.openUsage;
  const { t } = useTranslation();
  return useMemo(() => {
    if (!blocked) return [];
    const { reason, chips, firstUsage } = blocked;
    return [{
      key: 'answer-ref-source-blocked',
      severity: 'warning' as const,
      kind: 'defect' as const,
      category: t('blocks.answerRef.issueCategory'),
      detail: t('blocks.answerRef.issueSourceBlocked', {
        count: chips,
        // NOT lowercased: these read as whole phrases now ("Optional blocks not available for
        // recall"), so they are a second sentence rather than a clause spliced into the first.
        reason: t(`blocks.answerRef.blocked.${reason}`),
      }),
      goTo: openUsage && firstUsage
        ? () => openUsage(firstUsage.taskId, firstUsage.blockId)
        : undefined,
    }];
  }, [blocked, openUsage, t]);
}

/**
 * THE REFERENCES ONE BLOCK QUOTES, with what each could be set to.
 *
 * Read by the test card's stand-in control. Keyed by alias because that is the unit a teacher
 * names and sets: two chips in one block quoting one answer are one row, not two.
 */
export interface RecalledHere {
  aliasId: string;
  /** What the reference is called, as the chip shows it. */
  name: string;
  /** Every answer the source block could give, for the control to offer. */
  choices: AnswerChoice[];
  /** The teacher's own stand-in, when they have set one. */
  chosen?: unknown;
  /**
   * WHAT IS ON SCREEN BEFORE ANYONE SETS ANYTHING: the sampled correct answer.
   *
   * Without it the cycle has no idea where it is standing. It read `chosen`, which is absent
   * until the first click, found nothing, and stepped to the first choice — which for most
   * sources IS the correct answer already being shown, so the first click changed nothing and
   * the teacher had to click twice to see the cycle start.
   */
  resting: unknown;
  /**
   * THIS ENTRY IS THE DRIVEN AXIS, not a quoted value — the one whose consequence is the
   * whole question rather than a number inside it. Pinned first, and glyphed, for that.
   */
  driven?: boolean;
  /**
   * THE ANSWER AS THE PROSE WOULD READ IT, and the mark that says which answer it is.
   *
   * Resolved here rather than in the row, through the same `resolvedAt` a chip in a sentence
   * goes through, so the two cannot spell one answer two ways. The owner: "i prefer the
   * underlined text that we have inline in the prose ... recall chips should not be rendered
   * ... always render to text, with underline and tag as to what state it is".
   */
  shown?: { text: string; verdict: AnswerChoice['kind']; tier?: AnswerTier; trusted: boolean };
  /**
   * WHICH ANSWER THIS ENTRY IS SHOWING — `taskId:blockId:facet`, for dedupe.
   *
   * A block-address driver and a chip quoting the same block are two KEYS over one answer,
   * and the row printed both: two entries side by side reading the identical words. The key
   * cannot tell them apart — one is `ara_…` and the other an address — so the SOURCE does.
   */
  source?: string;
}

/**
 * THE BENCH KEY FOR A DRIVER THAT IS A PLAIN BLOCK ADDRESS.
 *
 * Stand-ins are keyed by ALIAS, and a block-address driver has no alias. Creating one on the
 * first pick was the first plan and is wrong: a test click would write to the document, and
 * the orphan sweep — which drops a name no chip cites, on leaving the block — would then
 * delete it again. So the address is its own key. The two spaces cannot collide: an alias id
 * begins `ara_`, a task id `tsk_`.
 *
 * No facet in it. `drivenVersionRoll` asks `answerAt(taskId, blockId)` and a block answers
 * once however many facets quote it, so a facet here would be a key nothing looks up.
 */
export const driverStandInKey = (taskId: string, blockId: string): string => `${taskId}:${blockId}`;

/**
 * WHERE A DRIVEN BLOCK'S ANSWER COMES FROM, as one source the row can offer.
 *
 * An alias driver resolves through its first live member, exactly as a chip does, and keys on
 * the ALIAS so a chip naming it and the driver entry are one state. A block driver keys on
 * its address. Null where the block is not driven, or where the address resolves to nothing —
 * which is the `orphaned` state the link control already reports, and not a value to set.
 */
function driverSourceOf(
  ctx: AnswerRefContextValue,
  block: Block,
): {
  key: string; name: string; block: Block; taskId: string; facet?: string;
  /**
   * THE ALIAS ITSELF, where the driver is one — because an answer cannot be placed without it.
   *
   * `block` is the alias's FIRST LIVE MEMBER, which is right for sampling an answer and wrong
   * for deciding which row that answer lands in: a union row is keyed to the equivalence class
   * (`ur_text:swamp`), not to any member's option id, so asking the member block produced an id
   * no version carries and the version in force fell back to the first one forever. Carried
   * here so `useDrivenVersionInForce` can route through `aliasDrivenRowId`, which is the
   * resolver that knows about classes.
   */
  alias?: AnswerRefAlias;
} | null {
  const address = parseDriverAddress((block.data as { drivenBy?: string } | undefined)?.drivenBy);
  if (!address) return null;
  if (address.kind === 'alias') {
    const alias = ctx.aliases.find(a => a.id === address.aliasId);
    if (!alias) return null;
    const hit = firstLiveMember(alias, ctx);
    if (!hit) return null;
    return {
      key: alias.id, name: alias.name ?? '', block: hit.block,
      taskId: hit.member.taskId, facet: hit.member.facet, alias,
    };
  }
  const task = ctx.tasks.find(tk => tk.id === address.taskId);
  const found = task?.blocks?.find(b => b.id === address.blockId);
  if (!found) return null;
  return {
    key: driverStandInKey(address.taskId, address.blockId),
    name: found.name ?? '', block: found, taskId: address.taskId, facet: address.facet,
  };
}

/**
 * WHICH VERSION A DRIVEN BLOCK IS SHOWING — chosen by the earlier answer, not by a pager.
 *
 * The axis had two pointers and they disagreed. The version pager moved the question; the
 * recall entry moved the answer and nothing else; and the row header showed whichever the
 * pager was on. Reported from a screenshot: "changing versions changes versions, but clicking
 * the answer link changes nothing ... these need to be united ... if it's a driven-by setup,
 * the link to the previous answer IS the version."
 *
 * It is also the student's rule, stated for the editor: answering "penguin" on the connected
 * question by definition only allows the penguin version to be seen. A pointer the teacher
 * could move independently was a state no student can ever be in.
 *
 * Null where the block is not driven, where the driver is gone, or outside authoring — each
 * of which leaves the stored pointers exactly as they were.
 */
export function useDrivenVersionInForce(block: Block | undefined): string | null {
  const ctx = useContext(AnswerRefContext);
  return useMemo(() => {
    if (!ctx || !block) return null;
    const source = driverSourceOf(ctx, block);
    if (!source) return null;
    const data = (source.block.data ?? {}) as Record<string, unknown>;
    /* The teacher's own pick, else the source's correct answer — the precedence every
       other reader of a stand-in follows, so the version and the chips agree. */
    const chosen = ctx.testAnswers?.[source.key];
    const answer = chosen !== undefined ? chosen : sampleCorrectAnswer(
      source.block.type, data,
      { variables: ctx.benchVariablesFor?.(source.taskId) ?? {} });
    const versions = (block.data as { versions?: { id: string; drivenRowId?: string }[] } | undefined)?.versions;
    if (!versions?.length) return null;
    /*
     * AN ALIAS PLACES ITS OWN ANSWERS, through the resolver that knows about classes.
     *
     * `drivenVersionIdFor` asks the DRIVER BLOCK which of its rows the answer is, and for an
     * alias that block is just the first live member — so it answered with a member option id
     * (`oa2`) while every version carries a union row id (`ur_text:bog`). Nothing ever matched,
     * the hook returned null, and both pointers fell back to version one: on an alias driver
     * the editor and the card showed the first row whatever the answer was. Found in the
     * browser on the first alias driver anyone had built.
     *
     * `aliasDrivenRowId` is the half that was already written for this and never called from
     * here. It walks the members itself — members are equivalents, so at most one has an
     * answer — and translates that member's own row into the union's.
     */
    const rowId = source.alias
      ? aliasDrivenRowId(
        source.alias,
        ctx.tasks as Parameters<typeof aliasDrivenRowId>[1],
        /*
         * THE STAND-IN IS OFFERED TO EVERY MEMBER, not only the one it was sampled from.
         *
         * A bench stand-in is keyed by the ALIAS — one answer for the whole equivalence —
         * because a student is on exactly one pathway and the teacher is standing in for
         * whichever that was. Handing it to the first live member alone meant an answer only
         * the SECOND member can place ("C", authored on Pathway 2) was offered to Pathway 1,
         * read as blank there, and never shown to anyone else: the pager stepped 1, 2, 4 and
         * the C row could not be opened.
         *
         * `aliasDrivenRowId` already resolves this correctly once asked — it skips a member
         * that reads blank and takes the first that places the answer, which is sound because
         * members are equivalents and at most one can really have been answered.
         */
        (taskId, blockId) => (source.alias!.members.some(
          m => m.taskId === taskId && m.blockId === blockId) ? answer : undefined),
        ctx.experienceVariableNames ?? new Set<string>())
      : drivenRowIdFor(source.block.type, data, answer, source.facet);
    if (rowId === null) return null;
    return versions.find(v => v.drivenRowId === rowId)?.id ?? null;
  }, [ctx, block]);
}

/**
 * WHICH ANSWER AN ENTRY SHOWS, as one comparable string — see `RecalledHere.source`.
 *
 * An absent facet reads as `value`, which is what a block with one answer has and what a
 * driver address leaves off: `parseDriverAddress` only carries a facet where the source has
 * several, while an alias member always names one. Without the default the driver entry and
 * a chip over the same MCQ compared `…:` against `…:value` and both were printed.
 *
 * `answerSourceKey` rather than a local copy of that one line: an alias union row's
 * `sourceOptions` are built from the same helper, and the cycle matches a row to a choice by
 * comparing them. Two spellings of this string is a cycle that silently finds nothing.
 */
const sourceKeyOf = answerSourceKey;

/**
 * WHAT THE ROW PRINTS FOR ONE ENTRY: the answer, and which answer it is.
 *
 * Through `resolvedAt` and `formatResolvedAnswer`, which is the pair a chip in a sentence
 * goes through — so the row and the prose cannot spell one answer two ways. Null where the
 * source resolves to nothing, which the row reads as "say the name instead".
 */
function shownOf(
  hit: { block: Block; member: AnswerRef },
  answer: unknown,
  variables: Record<string, number>,
  t: (k: string, o?: Record<string, unknown>) => string,
  locale: string,
  known?: AnswerChoice[],
): RecalledHere['shown'] {
  const resolved = resolvedAt(hit, answer, variables, known);
  if (!resolved) return undefined;
  return {
    /*
     * STRIPPED, because the row prints TEXT and an option's answer is authored html.
     * `formatResolvedAnswer` hands back what a sentence injects with `innerHTML`; rendered
     * as a React child it came out as the literal `<p>Flamingo</p>`, which is what the owner
     * photographed. A one-line label wants the words either way.
     */
    text: stripHtmlForLabel(formatResolvedAnswer(resolved, t, locale)),
    verdict: resolved.verdict,
    tier: resolved.tier,
    trusted: resolved.trusted,
  };
}

/**
 * NO ROLL IS PASSED, and it is worth saying why rather than leaving it to be noticed.
 *
 * A roll would decide two things here. Which option a branch currently marks correct — which
 * nothing displays, because the control offers the answers and not a verdict on them. And the
 * value behind "Correct answer" for a block whose key is a formula, which does need one.
 *
 * The roll that would answer the second belongs to the SOURCE task, not to the block being
 * tested, and the editor rolls per block — so the only roll reachable from here is the wrong
 * one. Rather than resolve against a roll that is not the right one, a row with nothing
 * pickable is not offered at all: option sources, which need no roll, work exactly; numeric
 * sources wait for the control that takes a typed value, which is what they actually need.
 */
/**
 * ONE CENSUS PER BLOCK PER CHANGE, shared by everything that asks.
 *
 * Three surfaces call `useRecalledHere` for the same block on every render — the card's cycle
 * handler, the Earlier answers row and the driven version control — and each `useMemo` is its
 * own. The work is a full alias walk plus, per cited alias, an answer enumeration and a
 * resolve, so three callers over eleven blocks is thirty-three of them for one click.
 * Measured at ~100ms a click on a small task, with long tasks behind it; the owner: "the
 * system has become extremely sluggish when clicking through driven-by."
 *
 * Keyed on the CONTEXT OBJECT, which the page rebuilds whenever anything the census reads
 * changes — the tasks, the aliases, the stand-ins. A `WeakMap` so a superseded context takes
 * its answers with it and nothing has to be invalidated by hand.
 */
const censusCache = new WeakMap<AnswerRefContextValue, Map<string, RecalledHere[]>>();

export function useRecalledHere(blockId: string | undefined): RecalledHere[] {
  const ctx = useContext(AnswerRefContext);
  const { t, i18n } = useTranslation();
  return useMemo(() => {
    const cached = ctx && blockId ? censusCache.get(ctx)?.get(blockId) : undefined;
    if (cached) return cached;
    const out = buildRecalledHere(ctx, blockId, t, i18n.language);
    if (ctx && blockId) {
      const forCtx = censusCache.get(ctx) ?? new Map<string, RecalledHere[]>();
      forCtx.set(blockId, out);
      censusCache.set(ctx, forCtx);
    }
    return out;
     
  }, [ctx, blockId, t, i18n.language]);
}

function buildRecalledHere(
  ctx: AnswerRefContextValue | null,
  blockId: string | undefined,
  t: (k: string, o?: Record<string, unknown>) => string,
  locale: string,
): RecalledHere[] {
  return ((): RecalledHere[] => {
    /*
     * NO BENCH, NO ROWS, AND NO WORK.
     *
     * Every consumer of this is a CONTROL for setting what a reference stands at — the recall
     * row and the chip's own cycle handler — so a surface with no setter can do nothing with
     * the answer. `setTestAnswer` is absent exactly there.
     *
     * It is also what makes the cost acceptable: this is a full alias census plus an answer
     * enumeration, it sits in the shared test bar, and ungated it ran for every input block
     * on every render. Measured as a cascade of 5s timeouts across `undo-sequences`, which
     * mounts many blocks and re-renders them hard. The gate used to live one layer up, in
     * the equation-only filter the row has since outgrown.
     */
    if (!ctx || !blockId || !ctx.setTestAnswer) return [];

    const block = ctx.tasks.flatMap(tk => tk.blocks ?? []).find(b => b.id === blockId);
    if (!block) return [];
    const cited = new Set(
      collectAliasUsages([{ id: '', blocks: [block] }] as never).map(u => u.aliasId),
    );
    /* A driven block with no chips at all still has one dependency, and it is the one with
       nothing on screen to click — so an empty `cited` is no longer the end of it. */
    if (cited.size === 0 && !(block.data as { drivenBy?: string } | undefined)?.drivenBy) return [];

    const out: RecalledHere[] = [];

    /*
     * THE DRIVER FIRST, when there is one, because it is the entry that changes the question
     * rather than a value inside it. It is the same kind of fact as the rest — what did they
     * answer — so it shares the row, the gesture and the storage; only its consequence
     * differs, and the glyph says so.
     *
     * An ALIAS driver keys on the alias, so a chip naming that alias and this entry are ONE
     * state and cannot disagree. A block driver keys on its address — see
     * `driverStandInKey`.
     */
    /*
     * BUILT EVEN WHERE A CHIP ALSO NAMES IT. Skipping a cited driver left the alias-driven
     * case with an ordinary entry: no glyph, no pinned place, and nothing saying that this
     * one choice moves the whole question. The loop below drops the duplicate instead.
     */
    const driverRef = driverSourceOf(ctx, block);
    if (driverRef) {
      const choices = answerChoicesFor(
        driverRef.block.type, (driverRef.block.data ?? {}) as Record<string, unknown>,
        { variables: ctx.benchVariablesFor?.(driverRef.taskId) ?? {}, facet: driverRef.facet },
      );
      if (choices.some(c => c.sample !== null)) {
        const vars = ctx.benchVariablesFor?.(driverRef.taskId) ?? {};
        const resting = sampleCorrectAnswer(
          driverRef.block.type, (driverRef.block.data ?? {}) as Record<string, unknown>, { variables: vars });
        const chosen = ctx.testAnswers?.[driverRef.key];
        out.push({
          aliasId: driverRef.key,
          name: driverRef.name,
          choices,
          chosen,
          resting,
          driven: true,
          source: sourceKeyOf(driverRef.taskId, driverRef.block.id, driverRef.facet),
          shown: shownOf(
            { block: driverRef.block, member: { taskId: driverRef.taskId, blockId: driverRef.block.id,
              facet: driverRef.facet ?? '' } },
            chosen !== undefined ? chosen : resting, vars, t, locale, choices),
        });
      }
    }

    /* What the driver entry already speaks for, so a chip quoting the same answer does not
       appear beside it saying the identical thing — the duplication the owner photographed. */
    const spoken = new Set(out.map(r => r.source).filter(Boolean));

    for (const alias of ctx.aliases) {
      if (!cited.has(alias.id)) continue;
      /* Already here as the driver, which is the alias-driven case: one state, one entry. */
      if (out.some(r => r.aliasId === alias.id)) continue;
      const hit = firstLiveMember(alias, ctx);
      // A reference pointing at nothing is already reported as broken, and offering to set a
      // value for it would say the problem is the value.
      if (!hit) continue;
      const choices = answerChoicesFor(
        hit.block.type, (hit.block.data ?? {}) as Record<string, unknown>,
        // The facet the reference names — FITB's answers differ per blank.
        { variables: ctx.benchVariablesFor?.(hit.member.taskId) ?? {}, facet: hit.member.facet },
      );
      // A row offering nothing but a disabled entry is a row that says nothing. See above.
      if (!choices.some(c => c.sample !== null)) continue;
      const vars = ctx.benchVariablesFor?.(hit.member.taskId) ?? {};
      const chosen = ctx.testAnswers?.[alias.id];
      const resting = sampleCorrectAnswer(
        hit.block.type, (hit.block.data ?? {}) as Record<string, unknown>, { variables: vars });
      const source = sourceKeyOf(hit.member.taskId, hit.block.id, hit.member.facet);
      /* The driver already shows this exact answer — see `RecalledHere.source`. */
      if (spoken.has(source)) continue;
      out.push({
        aliasId: alias.id,
        name: aliasDisplayName(alias, ctx, t),
        choices,
        source,
        shown: shownOf(hit, chosen !== undefined ? chosen : resting, vars, t, locale, choices),
        chosen: ctx.testAnswers?.[alias.id],
        // The same sample the chip is rendering — `sampleForAlias` builds the display from
        // this call, so the cycle starts from the answer the teacher can actually see.
        resting: sampleCorrectAnswer(
          hit.block.type, (hit.block.data ?? {}) as Record<string, unknown>,
          { variables: ctx.benchVariablesFor?.(hit.member.taskId) ?? {} },
        ),
      });
    }
    return out;
  })();
}

/** Setting a stand-in, for the control that offers one. Absent on surfaces with no bench. */
/**
 * REROLL EVERY RECALLED ANSWER, each inside the tier it is already showing.
 *
 * What Random means for a recall. The variables get a fresh draw and the recalled answers move
 * with them — but the chip's cycle has already said WHICH KIND of answer is being tested, and
 * a teacher who set one to a plausible mistake to walk an error-propagation pathway must not
 * have that undone by rerolling. So the number changes and the kind does not;
 * `rerollAnswerInTier` reads the kind back out of the current value rather than needing it
 * tracked anywhere.
 *
 * Writes through `setTestAnswer`, which is the one place a chip's chosen value lives — so the
 * sentence and the equation both pick it up without either being told separately.
 *
 * Silent where it cannot help: a source with no tileable band (an exact answer with no
 * tolerance), or one whose answer is not a single number (a slider's two handles, a GPS
 * coordinate). Leaving the value alone beats substituting one the teacher did not ask for.
 */
export function useRerollRecalls(): () => void {
  const ctx = useContext(AnswerRefContext);
  return useCallback(() => {
    if (!ctx?.setTestAnswer) return;
    for (const alias of ctx.aliases) {
      const hit = firstLiveMember(alias, ctx);
      if (!hit) continue;
      const bench = ctx.benchVariablesFor?.(hit.member.taskId) ?? {};
      const data = (hit.block.data ?? {}) as Record<string, unknown>;
      // Whatever is showing now: the teacher's cycled value if they picked one, else the
      // sample — the same precedence the sentence and the equation already read with.
      const current = ctx.testAnswers?.[alias.id]
        ?? sampleCorrectAnswer(hit.block.type, data, { variables: bench });
      const next = rerollAnswerInTier(hit.block.type, data, { variables: bench }, current);
      if (next !== null) ctx.setTestAnswer(alias.id, next);
    }
  }, [ctx]);
}

export function useSetTestAnswer(): ((aliasId: string, answer: unknown | null) => void) | undefined {
  return useContext(AnswerRefContext)?.setTestAnswer;
}

/**
 * WHETHER THIS REFERENCE'S ANSWER TAKES A DIGIT COUNT, which `formatsFor` cannot say.
 *
 * Digits are typed into two rows rather than picked from the menu, so they are not among the
 * formats — the question "is there anything to choose" and the question "is this a number"
 * are different ones, and one list cannot answer both.
 */
export function useAnswerRefTakesDigits(aliasId: string): boolean {
  return useAnswerRefDigitRows(aliasId).value;
}

/**
 * THE DIGIT ROWS THIS CHIP SHOULD DRAW, and the ceiling on what they may ask for.
 *
 * Three facts from one lookup, because they are all about the same source block and asking
 * three times is three chances to answer about different ones — the drift this codebase has
 * found twice in this feature already.
 *
 *   - `value`       — a numeric facet at all
 *   - `uncertainty` — a second, independent row, which only a Numerical `full` chip has
 *   - `maxDecimals` — a sensor's own readout precision, so a row cannot ask for digits the
 *                     instrument never measured
 */
export function useAnswerRefDigitRows(aliasId: string): {
  value: boolean;
  uncertainty: boolean;
  maxDecimals: number | null;
} {
  const ctx = useContext(AnswerRefContext);
  return useMemo(() => {
    const none = { value: false, uncertainty: false, maxDecimals: null };
    if (!ctx) return none;
    const alias = ctx.aliases.find(a => a.id === aliasId);
    const hit = alias && firstLiveMember(alias, ctx);
    if (!hit) return none;
    return {
      value: acceptsDigits(hit.block.type, hit.member.facet),
      uncertainty: acceptsUncertaintyDigits(
        hit.block.type, hit.member.facet, (hit.block.data ?? {}) as Record<string, unknown>),
      maxDecimals: maxDigitsFor(hit.block.type),
    };
  }, [ctx, aliasId]);
}

/**
 * THE FORMATS THIS REFERENCE COULD READ IN, or none.
 *
 * Answered from the SOURCE block, because what a format is depends on what is being quoted:
 * words have a case, a bearing has a compass point, a coordinate has a hemisphere, a plain
 * number has nothing. The chip asks; it has no table of its own.
 *
 * Fewer than two means there is nothing to choose between, and the chip offers no control at
 * all rather than a menu with one entry in it.
 */
export function useAnswerRefFormats(aliasId: string): AnswerFormat[] {
  const ctx = useContext(AnswerRefContext);
  return useMemo(() => {
    const alias = ctx?.aliases.find(a => a.id === aliasId);
    if (!ctx || !alias) return [];
    const hit = firstLiveMember(alias, ctx);
    if (!hit) return [];
    const formats = formatsFor(hit.block.type, hit.member.facet);
    return formats.length > 1 ? formats : [];
  }, [ctx, aliasId]);
}

/**
 * SETTING A REFERENCE'S ANSWER BY HAND, under Preview.
 *
 * The second of the two ways to fill a reference the run has not reached — walking the source
 * is the first and needs nothing. Delegated, in the capture phase, for the same two reasons
 * the editor's `useRecallPickHandler` is: the value is rendered as an html STRING so there is
 * no React handler to attach, and the prose it sits in stops clicks on their way up.
 *
 * CYCLES rather than opening a menu, which is the gesture already settled on the editor's test
 * card. One extra stop here, and it is the one that makes it safe: past the last answer the
 * cycle returns to NOTHING SET, so a teacher can always put the sentence back to the state a
 * student who has not reached the source would see.
 *
 * It offers only answers the source can actually give, and only where a block type enumerates
 * them — a number against a tolerance gives one accepted and one rejected, an option block
 * gives its options. A source offering nothing pickable is left alone rather than given a
 * control that does nothing.
 */
export function useStandInPickHandler(): (e: React.MouseEvent) => void {
  const ctx = useContext(AnswerRefContext);
  const { standIns, set } = useRecallStandIns();
  return (e: React.MouseEvent) => {
    const hit = (e.target as HTMLElement | null)?.closest?.('[data-answer-ref-standin]');
    if (!hit || !set || !ctx) return;
    e.stopPropagation();
    e.preventDefault();
    const aliasId = hit.getAttribute('data-answer-ref-standin')!;
    const alias = ctx.aliases.find(a => a.id === aliasId);
    if (!alias) return;
    const source = firstLiveMember(alias, ctx);
    if (!source) return;
    const usable = answerChoicesFor(
      source.block.type, (source.block.data ?? {}) as Record<string, unknown>,
      { variables: ctx.benchVariablesFor?.(source.member.taskId) ?? {}, facet: source.member.facet },
    ).filter(c => c.sample !== null);
    if (usable.length === 0) return;
    /*
     * The cycle is the answers and then NOTHING SET, which is the stop at `usable.length`.
     *
     * Where the cycle is standing is read from the current value rather than from a counter,
     * so it stays honest when a stand-in is cleared from somewhere else — and `findIndex`
     * answering -1 is doing real work at both ends: it covers "nothing set yet" and "set to
     * something this source can no longer give", and -1 + 1 is the first answer, which is
     * what both of those should step to.
     */
    const at = usable.findIndex(c => JSON.stringify(c.sample) === JSON.stringify(standIns[aliasId]));
    const next = (at + 1) % (usable.length + 1);
    set(aliasId, next === usable.length ? null : usable[next]!.sample);
  };
}
